import { dataServices, dispatcher, OrderData, Products, Promise, services } from '@ambita/ambita-components-core';
import * as landRegistryConstants from '../constants/landRegistry';
import * as landRegistryDataService from '../dataServices/landRegistryProducts';
import { LandRegistryLookupParts, LandRegistryLookupState } from '../typings/LandRegistryLookup';
import '../views/landRegistry/landRegistryTranslations';
import { setError } from '../services/integration';

const { log } = services;
const { realty } = dataServices;
const { actions: landRegistryActions, types: landRegistryTypes } = landRegistryConstants;

const extractMunicipalityNumber = (cadastreKey: string): string => {
  return cadastreKey.slice(0, cadastreKey.indexOf('-'));
};

const createPantebokProductOrderData = (
  reference: string,
  product: Products.Product,
  orderData: OrderData.Data
): Promise<any> => {
  const orderCollectionData = {
    reference,
    orderProducts: [
      {
        productCode: product.code,
        orderData
      }
    ]
  };

  return Promise.resolve(orderCollectionData);
};

export const registerTagLoading = (
  landRegistryPartName: LandRegistryLookupParts,
  landRegistryPartState: LandRegistryLookupState
): void => {
  dispatcher.dispatch({
    actionType: landRegistryActions.LAND_REGISTRY_TAG_LOADING,
    landRegistryPartName,
    landRegistryPartState
  });
};

export const registerTagLoadingFinished = (
  landRegistryPartName: LandRegistryLookupParts,
  landRegistryPartState: LandRegistryLookupState
): void => {
  dispatcher.dispatch({
    actionType: landRegistryActions.LAND_REGISTRY_TAG_LOADING_COMPLETE,
    landRegistryPartName,
    landRegistryPartState
  });
};

const landRegistryPromises = {
  ENCUMBRANCES: {},
  EASEMENTS: {},
  TITLES: {},
  LANDDATA: {},
  MONETARY_ENCUMBRANCES: {},
  PRIVILEGES: {}
};

export const retrieveLandRegistry = (
  landRegistryPartName: LandRegistryLookupParts,
  cadastreKey: string,
  landRegistryPartState: LandRegistryLookupState
): Promise<any> => {
  if (landRegistryPartState in landRegistryPromises[landRegistryPartName]) {
    landRegistryPromises[landRegistryPartName][landRegistryPartState].cancel();
  }

  dispatcher.dispatch({
    actionType: landRegistryActions.SET_LOADING_STATUS,
    landRegistryPartName,
    landRegistryPartState,
    component: landRegistryTypes[landRegistryPartName]
  });

  return landRegistryPromises[landRegistryPartName][landRegistryPartState] =
    realty.getLandRegistry(cadastreKey, landRegistryPartName, landRegistryPartState)
      .then(response => {
        dispatcher.dispatch({
          actionType: landRegistryActions.STORE_LAND_REGISTRY_PART,
          landRegistryPartName,
          response,
          cadastreKey,
          knr: extractMunicipalityNumber(cadastreKey),
          landRegistryPartState
        });
      })
      .catch(error => {
        let errorMessage = error && error.errorData && error.errorData.error && error.errorData.error.message;

        const thereIsRegularErrorMessage = error && error.message;
        if (!errorMessage && thereIsRegularErrorMessage) {
          errorMessage = error.message;
        }

        if (errorMessage && errorMessage.indexOf('Found too many bought ownerships') > -1) {
          errorMessage = services.i18n.getCurrentTranslation().landRegistry.tooManyOwnershipsError;
        }

        log.error(`Error while retrieving part ${landRegistryPartName} of land registry`, error);

        dispatcher.dispatch({
          actionType: landRegistryActions.STORE_LAND_REGISTRY_PART_ERROR,
          landRegistryPartName,
          landRegistryPartState,
          errorType: errorMessage
        });

        setError(errorMessage);
      });
};

let creationOfRightsPromise: Promise<any>;

export const retrieveCreationOfRights = (rightsKey: string): Promise<any> => {
  if (creationOfRightsPromise) {
    creationOfRightsPromise.cancel();
  }

  dispatcher.dispatch({
    actionType: landRegistryActions.SET_LOADING_STATUS,
    component: landRegistryTypes.RIGHT
  });

  return creationOfRightsPromise = realty.getCreationOfRights(rightsKey)
    .then(creationOfRights => {
      dispatcher.dispatch({
        actionType: landRegistryActions.STORE_RIGHT,
        right: creationOfRights
      });
    })
    .catch(error => {
      log.error('Land registry retrieve creationOfRights error', error);
      dispatcher.dispatch({
        actionType: landRegistryActions.STORE_RIGHT_ERROR,
        right: {}
      });
    });
};

export const addPantebokProduct = (
  reference: string,
  product: Products.Product,
  orderData: OrderData.Data,
  rightKey: string
): Promise<any> => {
  dispatcher.dispatch({
    actionType: landRegistryActions.PURCHASE_PANTEBOK,
    rightKey
  });

  return createPantebokProductOrderData(reference, product, orderData)
    .then(landRegistryDataService.confirmPantebokProductOrder)
    .then(() => {
      dispatcher.dispatch({
        actionType: landRegistryActions.PURCHASED_PANTEBOK,
        rightKey
      });
    })
    .catch(() => {
      dispatcher.dispatch({
        actionType: landRegistryActions.PURCHASE_PANTEBOK_FAILED,
        rightKey
      });

      return Promise.reject(new Error('Failed to purchase pantebok'));
    });
};

export const setOrderReference = (orderReference: string): void => {
  dispatcher.dispatch({
    actionType: landRegistryActions.SET_ORDER_REFERENCE,
    orderReference
  });
};
